import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError, API, downloadPdf, downloadFile } from "@/lib/api";
import { fmtDur, todayNZ, TIME_OPTIONS, WEEKDAY_LABELS, fmtDMY } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { HolidayList } from "@/components/HolidayList";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, Download, ClipboardCheck, SlidersHorizontal, ChevronLeft, ChevronRight, Lock, LockOpen, FileDown, Mail, FileSignature, CheckCircle2, Clock, ShieldCheck, TriangleAlert, Save, BellRing } from "lucide-react";
import { WeeklyTimesheetDialog } from "@/components/WeeklyTimesheetDialog";
import { ComplianceSettingsCard } from "@/components/ComplianceSettingsCard";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const WEEKDAYS = [
  { v: 0, l: "Monday" }, { v: 1, l: "Tuesday" }, { v: 2, l: "Wednesday" },
  { v: 3, l: "Thursday" }, { v: 4, l: "Friday" }, { v: 5, l: "Saturday" }, { v: 6, l: "Sunday" },
];

const shiftDate = (iso, days) => {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const fmtRange = (s, e) => {
  const opt = { day: "numeric", month: "short" };
  const sd = new Date(s + "T00:00:00");
  const ed = new Date(e + "T00:00:00");
  const yr = ed.getFullYear();
  return `${sd.toLocaleDateString("en-NZ", opt)} – ${ed.toLocaleDateString("en-NZ", opt)} ${yr}`;
};

export default function Timesheets() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [date, setDate] = useState(todayNZ());
  const [exporting, setExporting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["timesheets-period", date],
    queryFn: async () => (await api.get(`/timesheets/period?date=${date}`)).data,
    refetchInterval: 30000,
  });

  const rows = data?.rows || [];
  const periodStart = data?.period_start;
  const periodEnd = data?.period_end;
  const locked = !!data?.locked;
  const periodLabel = { weekly: "Week", fortnightly: "Fortnight", monthly: "Month" }[data?.pay_period] || "Period";

  const lockMut = useMutation({
    mutationFn: (lock) => api.post(`/timesheets/period/${lock ? "lock" : "unlock"}`, { date }),
    onSuccess: (_r, lock) => {
      toast.success(lock ? t("Pay period locked") : t("Pay period unlocked"));
      qc.invalidateQueries({ queryKey: ["timesheets-period"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const exportCsv = async () => {
    if (!periodStart) return;
    setExporting(true);
    try {
      const res = await fetch(`${API}/timesheets/export?start=${periodStart}&end=${periodEnd}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `timesheets_${periodStart}_${periodEnd}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t("Payroll CSV downloaded"));
    } catch (e) {
      toast.error(t("Could not export timesheets"));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">{t("Timesheets")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("Shift = first start → last finish, less unpaid lunch. Set the pay period on the Cleaners page.")}
        </p>
      </div>

      <Tabs defaultValue="signoff" className="w-full">
        <TabsList data-testid="timesheets-tabs" className="grid w-full grid-cols-4">
          <TabsTrigger value="signoff" data-testid="tab-signoff" className="text-xs sm:text-sm">{t("Sign-off")}</TabsTrigger>
          <TabsTrigger value="period" data-testid="tab-period" className="text-xs sm:text-sm">{t("Pay period")}</TabsTrigger>
          <TabsTrigger value="compliance" data-testid="tab-compliance" className="text-xs sm:text-sm">{t("Compliance")}</TabsTrigger>
          <TabsTrigger value="rules" data-testid="tab-rules" className="text-xs sm:text-sm">{t("Rules")}</TabsTrigger>
        </TabsList>

        <TabsContent value="signoff" className="mt-4 animate-in fade-in-50 duration-200">
          <WeeklySignoff />
        </TabsContent>

        <TabsContent value="period" className="mt-4 animate-in fade-in-50 duration-200">
          <div className="mb-4 flex justify-end">
            <Button onClick={exportCsv} disabled={exporting || !periodStart} data-testid="export-timesheets-btn" className="gap-2">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {t("Export this {{period}}", { period: t(periodLabel) })}
            </Button>
          </div>

          {/* Period navigator */}
          <div className="flex items-center justify-between rounded-md border border-border bg-card px-3 py-2.5">
            <Button
              variant="ghost" size="sm"
              onClick={() => periodStart && setDate(shiftDate(periodStart, -1))}
              data-testid="period-prev"
              className="gap-1"
            >
              <ChevronLeft className="h-4 w-4" /> {t("Prev")}
            </Button>
            <div className="text-center" data-testid="period-label">
              <div className="label-caps flex items-center justify-center gap-1.5 text-muted-foreground">
                {t(periodLabel)}
                {locked && (
                  <span data-testid="period-locked-badge" className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-700">
                    <Lock className="h-3 w-3" /> {t("Locked")}
                  </span>
                )}
              </div>
              <div className="font-display text-base font-extrabold">
                {periodStart ? fmtRange(periodStart, periodEnd) : "…"}
              </div>
              <Button
                variant="ghost" size="sm"
                onClick={() => lockMut.mutate(!locked)}
                disabled={lockMut.isPending || !periodStart}
                data-testid={locked ? "unlock-period-btn" : "lock-period-btn"}
                className="mt-0.5 h-7 gap-1 text-xs"
              >
                {locked ? <><LockOpen className="h-3.5 w-3.5" /> {t("Unlock")}</> : <><Lock className="h-3.5 w-3.5" /> {t("Lock period")}</>}
              </Button>
              {locked && data?.locked_by && (
                <div className="mt-0.5 text-[10px] text-muted-foreground" data-testid="period-lock-record">
                  {t("Locked by {{name}}", { name: data.locked_by })}{data.locked_at ? ` · ${new Date(data.locked_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}` : ""}
                </div>
              )}
            </div>
            <Button
              variant="ghost" size="sm"
              onClick={() => periodEnd && setDate(shiftDate(periodEnd, 1))}
              data-testid="period-next"
              className="gap-1"
            >
              {t("Next")} <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : rows.length === 0 ? (
            <Card className="mt-4 flex flex-col items-center gap-3 rounded-md border border-dashed py-20 text-center">
              <ClipboardCheck className="h-8 w-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">{t("No cleaner activity recorded for this pay period.")}</p>
            </Card>
          ) : (
            <Card className="mt-4 overflow-hidden rounded-md border">
              <div className="hidden md:block">
                <Table data-testid="timesheets-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("Cleaner")}</TableHead>
                      <TableHead>{t("Days")}</TableHead>
                      <TableHead>{t("Worked")}</TableHead>
                      <TableHead>{t("Break (paid)")}</TableHead>
                      <TableHead>{t("Lunch (unpaid)")}</TableHead>
                      <TableHead>{t("Clean time")}</TableHead>
                      <TableHead>{t("Unassigned")}</TableHead>
                      <TableHead className="text-right">{t("Rooms")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r.cleaner_id} data-testid={`timesheet-row-${r.cleaner_id}`}>
                        <TableCell className="font-medium">{r.cleaner_name}</TableCell>
                        <TableCell data-testid={`days-${r.cleaner_id}`}>{r.days_worked}</TableCell>
                        <TableCell className="font-semibold">{fmtDur(r.worked_seconds)}</TableCell>
                        <TableCell>{fmtDur(r.break_seconds)}</TableCell>
                        <TableCell className="text-orange-700" data-testid={`lunch-${r.cleaner_id}`}>
                          {fmtDur(r.lunch_seconds)}
                        </TableCell>
                        <TableCell>{fmtDur(r.clean_seconds)}</TableCell>
                        <TableCell data-testid={`unassigned-${r.cleaner_id}`} className="text-muted-foreground">
                          {fmtDur(r.unassigned_seconds)}
                        </TableCell>
                        <TableCell className="text-right">
                          {r.rooms_completed}/{r.rooms_total}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile: one card per cleaner (the 8-column table is unreadable on a phone) */}
              <div className="divide-y divide-border md:hidden" data-testid="timesheets-mobile">
                {rows.map((r) => (
                  <div key={r.cleaner_id} className="space-y-2 p-3" data-testid={`timesheet-mrow-${r.cleaner_id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold">{r.cleaner_name}</span>
                      <span className="text-xs text-muted-foreground">{t("{{count}} day(s)", { count: r.days_worked })}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">{t("Worked")}</span>
                        <span className="font-semibold">{fmtDur(r.worked_seconds)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">{t("Rooms")}</span>
                        <span>{r.rooms_completed}/{r.rooms_total}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">{t("Break (paid)")}</span>
                        <span>{fmtDur(r.break_seconds)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">{t("Lunch (unpaid)")}</span>
                        <span className="text-orange-700">{fmtDur(r.lunch_seconds)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">{t("Clean time")}</span>
                        <span>{fmtDur(r.clean_seconds)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">{t("Unassigned")}</span>
                        <span>{fmtDur(r.unassigned_seconds)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="compliance" className="mt-4 animate-in fade-in-50 duration-200">
          <BreakCompliance />
        </TabsContent>

        <TabsContent value="rules" className="mt-4 animate-in fade-in-50 duration-200">
          <PayrollRulesCard />
          <div className="mt-4"><HolidaySettingsCard /></div>
          <div className="mt-4"><ComplianceSettingsCard /></div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function HolidaySettingsCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [country, setCountry] = useState(null);
  const [region, setRegion] = useState("");

  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
  });
  const { data: options } = useQuery({
    queryKey: ["holiday-options"],
    queryFn: async () => (await api.get("/holiday-options")).data,
    staleTime: 600000,
  });
  const [rule, setRule] = useState("off");
  useEffect(() => {
    if (settings && country === null) {
      setCountry(settings.holiday_country || "NZ");
      setRegion(settings.holiday_region || "");
      setRule(settings.public_holiday_rule || "off");
    }
  }, [settings]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: (body) => api.put("/settings", body),
    onSuccess: () => {
      toast.success(t("Public holiday settings saved"));
      qc.invalidateQueries({ queryKey: ["settings"] });
      qc.invalidateQueries({ queryKey: ["holidays"] });
      qc.invalidateQueries({ queryKey: ["roster-day"] });
      qc.invalidateQueries({ queryKey: ["weekly-timesheet"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  if (country === null) return null;
  const countries = options?.countries || [];
  const off = country === "OFF";
  const current = countries.find((c) => c.code === country);
  const subdivs = current?.subdivisions || [];

  return (
    <Card className="rounded-md border p-4" data-testid="holiday-settings-card">
      <div className="mb-3 flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-muted-foreground" />
        <span className="label-caps text-muted-foreground">{t("Public holidays")}</span>
      </div>
      <p className="mb-3 text-[11px] text-muted-foreground">
        {t("Recognised public holidays are auto-labelled on timesheets so they're not mistaken for normal days. Pick your country (and region for the local anniversary day), or turn it off.")}
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label className="text-xs">{t("Country")}</Label>
          <Select value={country} onValueChange={(v) => { setCountry(v); setRegion(""); }}>
            <SelectTrigger className="h-9" data-testid="holiday-country"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="OFF">{t("Off — ignore public holidays")}</SelectItem>
              {countries.map((c) => (
                <SelectItem key={c.code} value={c.code}>{c.name === c.code ? c.code : `${c.name} (${c.code})`}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {!off && subdivs.length > 0 && (
          <div className="space-y-1.5">
            <Label className="text-xs">{t("Region / state (for local anniversary days)")}</Label>
            <Select value={region || "__all"} onValueChange={(v) => setRegion(v === "__all" ? "" : v)}>
              <SelectTrigger className="h-9" data-testid="holiday-region"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="__all">{t("Whole country (national only)")}</SelectItem>
                {subdivs.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">{t("Hamilton/Waikato observes Auckland Anniversary — use AUK.")}</p>
          </div>
        )}
      </div>
      {!off && (
        <div className="mt-4 space-y-1.5" data-testid="holiday-rule">
          <Label className="text-xs">{t("On a public holiday, cleaners are…")}</Label>
          <Select value={rule} onValueChange={setRule}>
            <SelectTrigger className="h-9" data-testid="holiday-rule-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="off">{t("Off by default — manager adds anyone who agrees to work (from the Rostered-off panel)")}</SelectItem>
              <SelectItem value="rostered">{t("Rostered as normal — public holidays are ordinary working days")}</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[11px] text-muted-foreground">
            {t("Timesheets mark worked holidays as \"Public holiday worked\" and unworked ones as \"Public holiday\" or \"Public holiday — rostered day off\", so payroll can apply the right entitlements.")}
          </p>
        </div>
      )}
      {!off && <HolidayList />}
      <div className="mt-4 flex justify-end">
        <Button
          onClick={() => save.mutate({ holiday_country: country, holiday_region: off ? "" : region, public_holiday_rule: rule })}
          disabled={save.isPending}
          data-testid="holiday-save"
          className="gap-2"
        >
          {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {t("Save")}
        </Button>
      </div>
    </Card>
  );
}

function PayrollRulesCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [form, setForm] = useState(null);
  const { data } = useQuery({
    queryKey: ["payroll-settings"],
    queryFn: async () => (await api.get("/payroll-settings")).data,
  });
  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const save = useMutation({
    mutationFn: (body) => api.put("/payroll-settings", body),
    onSuccess: () => {
      toast.success(t("Payroll rules saved"));
      qc.invalidateQueries({ queryKey: ["payroll-settings"] });
      qc.invalidateQueries({ queryKey: ["timesheets-period"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  if (!form) return null;
  const num = (k) => (v) => setForm({ ...form, [k]: v === "" ? 0 : Number(v) });
  const al = { enabled: false, weekday: 6, time: "17:00", every_weeks: 1, anchor: null, ...(form.auto_lock || {}) };
  const setAL = (patch) => setForm({ ...form, auto_lock: { ...al, ...patch } });
  const sr = { enabled: true, mode: "weekday", weekday: 6, time: "18:00", ...(form.sign_reminder || {}) };
  const setSR = (patch) => setForm({ ...form, sign_reminder: { ...sr, ...patch } });
  const ds = (form.default_start_times && form.default_start_times.length === 7) ? form.default_start_times : ["", "", "", "", "", "", ""];
  const setDS = (idx, v) => { const next = [...ds]; next[idx] = v === "__blank" ? "" : v; setForm({ ...form, default_start_times: next }); };

  return (
    <Card className="rounded-md border p-4" data-testid="payroll-rules-card">
      <div className="mb-3 flex items-center gap-2">
        <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
        <span className="label-caps text-muted-foreground">{t("Payroll rules")}</span>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label className="text-xs">{t("Paid break cap (min)")}</Label>
          <Input
            type="number" min="0" value={form.break_cap_minutes}
            onChange={(e) => num("break_cap_minutes")(e.target.value)}
            data-testid="payroll-break-cap"
          />
          <p className="text-[11px] text-muted-foreground">{t("0 = no cap. Break beyond this is unpaid.")}</p>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">{t("Round shift to (min)")}</Label>
          <Input
            type="number" min="0" value={form.round_minutes}
            onChange={(e) => num("round_minutes")(e.target.value)}
            data-testid="payroll-round"
          />
          <p className="text-[11px] text-muted-foreground">{t("0 = no rounding of start/finish.")}</p>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">{t("Auto lunch (min)")}</Label>
          <Input
            type="number" min="0" value={form.auto_lunch_minutes}
            onChange={(e) => num("auto_lunch_minutes")(e.target.value)}
            data-testid="payroll-auto-lunch"
          />
          <p className="text-[11px] text-muted-foreground">{t("Deducted unless a lunch was logged, or the cleaner ticks \"No lunch\".")}</p>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">{t("Break travel offset (min)")}</Label>
          <Input
            type="number" min="0" value={form.break_offset_minutes ?? 5}
            onChange={(e) => num("break_offset_minutes")(e.target.value)}
            data-testid="payroll-break-offset"
          />
          <p className="text-[11px] text-muted-foreground">{t("Added to the 10-min rest break for travel (Quantum = 5 → 15-min breaks).")}</p>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">{t("Auto-end rest break after (min)")}</Label>
          <Input
            type="number" min="0" value={form.auto_break_end_minutes ?? 15}
            onChange={(e) => num("auto_break_end_minutes")(e.target.value)}
            data-testid="payroll-auto-break-end"
          />
          <p className="text-[11px] text-muted-foreground">{t("Break ends itself at this time and the cleaner is back on the clock. 0 = cleaner ends it manually.")}</p>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">{t("Auto-end lunch after (min)")}</Label>
          <Input
            type="number" min="0" value={form.auto_lunch_end_minutes ?? 0}
            onChange={(e) => num("auto_lunch_end_minutes")(e.target.value)}
            data-testid="payroll-auto-lunch-end"
          />
          <p className="text-[11px] text-muted-foreground">{t("Default 0 = off: lunch is ended manually by the cleaner (30-min minimum still applies).")}</p>
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label className="text-xs">{t("Payroll email (for emailed timesheets)")}</Label>
          <Input
            type="email" value={form.payroll_email || ""}
            placeholder="payroll@yourmotel.co.nz"
            onChange={(e) => setForm({ ...form, payroll_email: e.target.value })}
            data-testid="payroll-email"
          />
          <p className="text-[11px] text-muted-foreground">{t("Timesheets are emailed to each cleaner and, if set, a copy here.")}</p>
        </div>
      </div>

      <div className="mt-4 rounded-md border border-border p-3" data-testid="default-start-section">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="h-4 w-4 text-muted-foreground" />
          <span className="label-caps text-muted-foreground">{t("Default start & finish (per day)")}</span>
        </div>
        <p className="mt-1 text-[11px] text-muted-foreground">
          {t("Pre-fills the Start time in 15-minute blocks (24-hour clock) when correcting a day that has no clock-in. Leave blank for none.")}
        </p>
        <div className="mt-3 grid gap-3 sm:grid-cols-4 lg:grid-cols-7">
          {WEEKDAY_LABELS.map((lbl, i) => (
            <div key={lbl} className="space-y-1.5">
              <Label className="text-xs">{t(lbl)}</Label>
              <Select value={ds[i] || "__blank"} onValueChange={(v) => setDS(i, v)}>
                <SelectTrigger className="h-8" data-testid={`default-start-${i}`}><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  <SelectItem value="__blank">—</SelectItem>
                  {TIME_OPTIONS.filter(Boolean).map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-center justify-between gap-2 rounded-md border border-dashed border-border bg-secondary/30 px-3 py-2">
          <div>
            <div className="text-xs font-semibold">{t("Auto-fill finish from estimates")}</div>
            <p className="text-[11px] text-muted-foreground">{t("When a worked day has no finish, set it to the default start + a fair share of that day's estimated workload (split across cleaners), snapped to 15 min.")}</p>
          </div>
          <Switch checked={!!form.auto_default_finish} onCheckedChange={(v) => setForm({ ...form, auto_default_finish: v })} data-testid="auto-default-finish" />
        </div>
      </div>

      <div className="mt-4 rounded-md border border-border p-3" data-testid="auto-lock-section">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-muted-foreground" />
            <span className="label-caps text-muted-foreground">{t("Auto-lock pay periods")}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{al.enabled ? t("On") : t("Off")}</span>
            <Switch checked={al.enabled} onCheckedChange={(v) => setAL({ enabled: v })} data-testid="auto-lock-enabled" />
          </div>
        </div>
        <p className="mt-1 text-[11px] text-muted-foreground">
          {t("When on, finished pay periods lock automatically at the deadline so no one can edit past timesheets after payroll runs. A manager can still unlock a period to make a correction.")} {t("A period with unsigned timesheets is not auto-locked — it stays open so cleaners can fix and sign first (it locks once everyone has signed).")}
        </p>
        {al.enabled && (
          <div className="mt-3 grid gap-4 sm:grid-cols-4">
            <div className="space-y-1.5">
              <Label className="text-xs">{t("Repeat")}</Label>
              <Select value={String(al.every_weeks)} onValueChange={(v) => setAL({ every_weeks: Number(v) })}>
                <SelectTrigger data-testid="auto-lock-cadence"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">{t("Weekly")}</SelectItem>
                  <SelectItem value="2">{t("Fortnightly")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t("Lock day")}</Label>
              <Select value={String(al.weekday)} onValueChange={(v) => setAL({ weekday: Number(v) })}>
                <SelectTrigger data-testid="auto-lock-weekday"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {WEEKDAYS.map((d) => (<SelectItem key={d.v} value={String(d.v)}>{t(d.l)}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t("Lock time")}</Label>
              <Input type="time" value={al.time || "17:00"} onChange={(e) => setAL({ time: e.target.value })} data-testid="auto-lock-time" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t("Starting from")}</Label>
              <Input type="date" value={al.anchor || ""} onChange={(e) => setAL({ anchor: e.target.value })} data-testid="auto-lock-anchor" />
            </div>
            <p className="text-[11px] text-muted-foreground sm:col-span-4">
              {t("Checked every hour (Pacific/Auckland). Periods that ended on or before each deadline are locked once.")}
            </p>
          </div>
        )}
      </div>

      <div className="mt-4 rounded-md border border-dashed p-3" data-testid="sign-reminder-settings">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <BellRing className="h-4 w-4 text-muted-foreground" />
            <span className="label-caps text-muted-foreground">{t("Timesheet sign reminder")}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{sr.enabled ? t("On") : t("Off")}</span>
            <Switch checked={sr.enabled} onCheckedChange={(v) => setSR({ enabled: v })} data-testid="sign-reminder-enabled" />
          </div>
        </div>
        <p className="mt-1 text-[11px] text-muted-foreground">
          {t("Cleaners with an unsigned timesheet get an in-app message and an email at this time. Default: Sunday 6pm.")}
        </p>
        {sr.enabled && (
          <div className="mt-3 grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label className="text-xs">{t("Remind on")}</Label>
              <Select value={sr.mode} onValueChange={(v) => setSR({ mode: v })}>
                <SelectTrigger data-testid="sign-reminder-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekday">{t("A set weekday")}</SelectItem>
                  <SelectItem value="period_end">{t("Last day of the pay period")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {sr.mode === "weekday" && (
              <div className="space-y-1.5">
                <Label className="text-xs">{t("Day")}</Label>
                <Select value={String(sr.weekday)} onValueChange={(v) => setSR({ weekday: Number(v) })}>
                  <SelectTrigger data-testid="sign-reminder-weekday"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {WEEKDAYS.map((d) => (<SelectItem key={d.v} value={String(d.v)}>{t(d.l)}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs">{t("Time")}</Label>
              <Input type="time" step="3600" value={sr.time || "18:00"} onChange={(e) => setSR({ time: e.target.value })} data-testid="sign-reminder-time" />
              <p className="text-[11px] text-muted-foreground">{t("Sent on the hour (Pacific/Auckland).")}</p>
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 flex justify-end">
        <Button onClick={() => save.mutate(form)} disabled={save.isPending} data-testid="save-payroll-btn" className="gap-2">
          {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {t("Save rules")}
        </Button>
      </div>
    </Card>
  );
}

function WeeklySignoff() {
  const { t } = useTranslation();
  const [wk, setWk] = useState(todayNZ());
  const [view, setView] = useState(null); // { cleaner_id }
  const [emailing, setEmailing] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["weekly-all", wk],
    queryFn: async () => (await api.get(`/timesheets/weekly/all?date=${wk}`)).data,
  });
  const rows = data?.rows || [];

  const createCombinedPdf = async () => {
    setPdfBusy(true);
    try {
      await downloadPdf(`/timesheets/weekly/pdf?date=${wk}&cleaner_id=all`, `timesheets_${data?.week_end || wk}.pdf`);
    } catch {
      toast.error(t("Could not create the PDF. Please try again."));
    } finally {
      setPdfBusy(false);
    }
  };

  const createPerCleanerPdf = async () => {
    setPdfBusy(true);
    try {
      for (const r of rows) {
        const name = (r.cleaner_name || "cleaner").replace(/\s+/g, "_");
        await downloadPdf(
          `/timesheets/weekly/pdf?date=${wk}&cleaner_id=${r.cleaner_id}`,
          `timesheet_${name}_${data?.week_end || wk}.pdf`,
        );
      }
      toast.success(t("Created {{count}} PDF(s)", { count: rows.length }));
    } catch {
      toast.error(t("Could not create the PDFs. Please try again."));
    } finally {
      setPdfBusy(false);
    }
  };

  const exportCsv = async () => {
    try {
      await downloadFile(`/timesheets/weekly/csv?date=${wk}`, `timesheets_${data?.week_end || wk}.csv`);
    } catch {
      toast.error(t("Could not export the CSV. Please try again."));
    }
  };

  const exportXlsx = async () => {
    try {
      await downloadFile(
        `/timesheets/weekly/xlsx?date=${wk}`,
        `timesheets_${data?.week_end || wk}.xlsx`,
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      );
    } catch {
      toast.error(t("Could not export the Excel file. Please try again."));
    }
  };

  const emailAll = async () => {
    setEmailing(true);
    try {
      const res = await api.post(`/timesheets/weekly/email`, { date: wk });
      const s = res.data.sent?.length || 0;
      const err = res.data.errors?.length || 0;
      toast.success(t("Emailed {{count}} timesheet(s)", { count: s }) + (err ? t(", {{count}} failed", { count: err }) : ""));
      if (res.data.skipped?.length) toast.info(t("No email on file: {{names}}", { names: res.data.skipped.join(", ") }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setEmailing(false);
    }
  };

  return (
    <Card className="rounded-md border p-4" data-testid="weekly-signoff-card">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <FileSignature className="h-4 w-4 text-muted-foreground" />
          <span className="label-caps text-muted-foreground">{t("Weekly timesheets — sign-off")}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" className="gap-1.5" onClick={createCombinedPdf} disabled={pdfBusy || rows.length === 0} data-testid="create-pdf-combined-btn">
            {pdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} {t("Combined PDF")}
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={createPerCleanerPdf} disabled={pdfBusy || rows.length === 0} data-testid="create-pdf-per-cleaner-btn">
            {pdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} {t("PDF per cleaner")}
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={exportCsv} disabled={rows.length === 0} data-testid="export-csv-btn">
            <Download className="h-4 w-4" /> {t("Export CSV")}
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={exportXlsx} disabled={rows.length === 0} data-testid="export-xlsx-btn">
            <Download className="h-4 w-4" /> {t("Export Excel")}
          </Button>
          <Button size="sm" onClick={emailAll} disabled={emailing || rows.length === 0} className="gap-1.5" data-testid="email-all-btn">
            {emailing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} {t("Email timesheets")}
          </Button>
        </div>
      </div>

      <div className="mb-3 flex items-center justify-between rounded-md border border-border px-3 py-2">
        <Button variant="ghost" size="sm" onClick={() => setWk(shiftDate(wk, -7))} data-testid="wk-prev" className="gap-1"><ChevronLeft className="h-4 w-4" /> {t("Prev")}</Button>
        <div className="text-center">
          <div className="label-caps text-muted-foreground">{t("Week ending")}</div>
          <div className="font-display text-sm font-extrabold" data-testid="wk-label">{data?.week_end ? fmtDMY(data.week_end) : "…"}</div>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setWk(shiftDate(wk, 7))} data-testid="wk-next" className="gap-1">{t("Next")} <ChevronRight className="h-4 w-4" /></Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : rows.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">{t("No cleaners to show.")}</p>
      ) : (
        <div className="divide-y divide-border">
          {rows.map((r) => (
            <div key={r.cleaner_id} className="flex items-center justify-between gap-2 py-2.5" data-testid={`weekly-row-${r.cleaner_id}`}>
              <div className="min-w-0">
                <div className="truncate font-medium">{r.cleaner_name}</div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{r.total_hours_worked.toFixed(2)} {t("hrs")}</span>
                  {r.skipped_lunch_days > 0 && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-700"
                      data-testid={`skipped-lunch-${r.cleaner_id}`}
                      title="Entitled to a lunch break but didn't take one — check for compliance"
                    >
                      <TriangleAlert className="h-3 w-3" /> {t("Skipped lunch ({{days}}d)", { days: r.skipped_lunch_days })}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {r.authorised_at ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-700" data-testid={`status-${r.cleaner_id}`}><CheckCircle2 className="h-3 w-3" /> {t("Authorised")}</span>
                ) : r.cleaner_signed_at ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-700" data-testid={`status-${r.cleaner_id}`}><FileSignature className="h-3 w-3" /> {t("Signed — awaiting you")}</span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[11px] font-bold text-muted-foreground" data-testid={`status-${r.cleaner_id}`}><Clock className="h-3 w-3" /> {t("Unsigned")}</span>
                )}
                <Button variant="outline" size="sm" onClick={() => setView(r.cleaner_id)} data-testid={`view-ts-${r.cleaner_id}`}>{t("View")}</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {view && (
        <WeeklyTimesheetDialog
          open={!!view}
          onOpenChange={(o) => !o && setView(null)}
          mode="manager"
          cleanerId={view}
          date={wk}
        />
      )}
    </Card>
  );
}

function BreakCompliance() {
  const { t } = useTranslation();
  const [wk, setWk] = useState(todayNZ());
  const { data, isLoading } = useQuery({
    queryKey: ["compliance", wk],
    queryFn: async () => (await api.get(`/timesheets/compliance?date=${wk}`)).data,
  });
  const rows = data?.rows || [];
  const active = rows.filter((r) => r.days_worked > 0);

  return (
    <Card className="rounded-md border p-4" data-testid="break-compliance-card">
      <div className="mb-3 flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-muted-foreground" />
        <span className="label-caps text-muted-foreground">{t("Break compliance")}</span>
      </div>
      <p className="mb-3 text-[11px] text-muted-foreground">
        {t("Rest breaks run 10 min + travel offset (15 at Quantum); lunch is a 30-min minimum. Shows breaks taken vs entitled for the week.")}
      </p>

      <div className="mb-3 flex items-center justify-between rounded-md border border-border px-3 py-2">
        <Button variant="ghost" size="sm" onClick={() => setWk(shiftDate(wk, -7))} data-testid="cmp-prev" className="gap-1"><ChevronLeft className="h-4 w-4" /> {t("Prev")}</Button>
        <div className="text-center">
          <div className="label-caps text-muted-foreground">{t("Week ending")}</div>
          <div className="font-display text-sm font-extrabold" data-testid="cmp-label">{data?.week_end ? fmtDMY(data.week_end) : "…"}</div>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setWk(shiftDate(wk, 7))} data-testid="cmp-next" className="gap-1">{t("Next")} <ChevronRight className="h-4 w-4" /></Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : active.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">{t("No cleaner activity this week.")}</p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("Cleaner")}</TableHead>
                <TableHead>{t("Days")}</TableHead>
                <TableHead>{t("Hours")}</TableHead>
                <TableHead>{t("Rest (taken/due)")}</TableHead>
                <TableHead>{t("Lunch (taken/due)")}</TableHead>
                <TableHead className="text-right">{t("Status")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {active.map((r) => (
                <TableRow key={r.cleaner_id} data-testid={`compliance-row-${r.cleaner_id}`}>
                  <TableCell className="font-medium">{r.cleaner_name}</TableCell>
                  <TableCell>{r.days_worked}</TableCell>
                  <TableCell>{r.worked_hours.toFixed(2)}</TableCell>
                  <TableCell className={r.rest_taken < r.rest_entitled ? "font-semibold text-amber-700" : ""}>{r.rest_taken}/{r.rest_entitled}</TableCell>
                  <TableCell className={r.meal_taken < r.meal_entitled || r.short_lunch ? "font-semibold text-amber-700" : ""}>{r.meal_taken}/{r.meal_entitled}{r.short_lunch ? " ⚠" : ""}</TableCell>
                  <TableCell className="text-right">
                    {r.compliant ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-700" data-testid={`compliance-status-${r.cleaner_id}`}><CheckCircle2 className="h-3 w-3" /> {t("Compliant")}</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-700" data-testid={`compliance-status-${r.cleaner_id}`}><TriangleAlert className="h-3 w-3" /> {t("Review")}</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </Card>
  );
}

